<template>
  <Drawer
    title="Cancel Appointment?"
    back-arrow
    :backdrop="isBottom"
    :placement="placement"
    aria-label="Cancel appointment"
    @hide-drawer="emit('hide-drawer')"
  >
    <div class="px-4">
      <Alert class="mb-4">
        <div>
          This will notify
          <span class="font-bold">{{ appointment.customer.full_name }}</span>
          the appointment has been cancelled.
        </div>
      </Alert>
      <TextArea
        v-model="cancelReason"
        data-cy="cancel-reason"
        message="Provide a reason*"
        :validation="firstErrorMessage(v$.cancelReason) ? 'error' : ''"
        :helper-text="firstErrorMessage(v$.cancelReason)"
      />
    </div>
    <template #footer>
      <div class="flex gap-4 w-full">
        <Button
          label="Keep Appointment"
          type="Secondary"
          class="w-full"
          @click="emit('hide-drawer')"
        />
        <Button
          label="Cancel Appointment"
          type="Alert"
          class="w-full"
          data-cy="confirm-cancel"
          @click="handleCancel"
        />
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Button from "@/components/Seranova/Buttons/Button.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import { AppointmentV2 } from "@/typings/appointment";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";

const props = withDefaults(
  defineProps<{
    appointment: AppointmentV2;
    placement?: "right" | "bottom";
  }>(),
  { placement: "right" }
);
const emit = defineEmits(["hide-drawer", "refresh-appointment"]);

// This drawer is a SIBLING of the details drawer, not a child of it, so in
// right placement it deliberately carries no backdrop of its own: it slides
// over a panel that already has one, and a second scrim would double the dim.
//
// As a bottom sheet that reasoning inverts. Both panels sit at bottom-0 with
// the same z-index, so the only pixels the user can reach outside the cancel
// sheet belong to the DETAILS sheet's backdrop, whose click handler closes the
// details drawer and unmounts this subtree with it -- turning "tap outside to
// dismiss the cancel step" into "discard the cancel flow and close everything".
// Its own backdrop keeps that tap scoped to this sheet.
const isBottom = computed(() => props.placement === "bottom");
const store = useStore();
const cancelReason = ref("");
const v$ = useVuelidate(
  {
    cancelReason: {
      required: helpers.withMessage("Must provide a reason", required)
    }
  },
  {
    cancelReason
  }
);

const handleCancel = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;
  await store.dispatch("cancelAppointment", {
    id: props.appointment.id,
    reason: cancelReason.value
  });
  window.notify.success("Appointment cancelled");
  emit("refresh-appointment");
};
</script>

<style lang="scss" scoped></style>
